import React, { useState, useMemo, useCallback } from 'react';
import {
  Linking,
  Pressable,
  StyleProp,
  ViewStyle,
  ActivityIndicator,
} from 'react-native';
import styled, { css } from '@emotion/native';
import { BodyText, H1, H4 } from '@/shared/components/StyledText';
import {
  green,
  lightGreen,
  mint,
  mintGreen,
  sand,
  white,
} from '@/shared/styles/colors';
import { IS_WEB } from '@/shared/constants/utility';
import Button from '@/shared/components/Button';
import { formatCurrency, openSocialLink } from '@/shared/utils';
import useIsDesktop from '@/shared/hooks/useIsDesktop';
import { IsDesktopProps } from '@/shared/styles/types';
import useDimensions from '@/shared/hooks/useDimensions';
import { EMME_SUPPORT_EMAIL } from '@/shared/constants/utility';
import GalleryImageDisplay from './GalleryImageDisplay';
import { Maybe, NextAvailableSlot, Pricing } from '@/shared/types/generated';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import BackToTopButton from './BackToTopButton';
import { useScrollToTop } from '@/shared/hooks/useScrollToTop';
import { useRouter } from 'expo-router';
import { ROUTES } from '@/shared/constants/routes';
import { PageTitle } from '@/shared/styles/styledComponents/editProfileForm';
import PillTicker from './carousel/PillTicker';
import ScrollableView from '@/shared/components/ScrollableView';
import useStore from '@/stores/useStore';
import ModalWrapper from '../styles/components/ModalWrapper';
import ShareProfileUrl from '@/components/stylist-profile/ShareProfileUrl';
import { GALLERY_CONSTANTS } from '../styles/constants';
import { createResizedPhotoUrl, FULL_WIDTH } from '@/shared/utils/photoUtils';
import { fromUnixTime } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useGetUser } from '@/shared/hooks/useGetUser';
import { getCurrentTimeZone } from '@/shared/utils';
import PricingAccordion from './PricingAccordion';

interface StylistProfileProps {
  stylistName: string;
  expertise: string[];
  aesthetics: string[];
  profilePicUrl: string;
  instagramHandle?: string | null;
  tiktokHandle?: string | null;
  substackHandle?: string | null;
  bio?: string;
  landscapeImage: string | null;
  portraitImages: string[];
  showConsultationButton?: boolean;
  onClose?: () => void;
  pricing?: Pricing;
  customHourlyRate?: number;
  onConsultationButtonPress?: () => void;
  nextAvailableSlot?: Maybe<NextAvailableSlot>;
  showSubheaderSection?: boolean;
  outerContainerStyle?: StyleProp<ViewStyle>;
  showPreviewLabel?: boolean;
  isModalView?: boolean;
  shareProfileUrl?: string;
  isPreviewView?: boolean;
  customInPersonHourlyRate?: number;
  isAvailableInPerson?: boolean;
}

const StylistProfile: React.FC<StylistProfileProps> = ({
  stylistName,
  expertise,
  aesthetics,
  profilePicUrl,
  instagramHandle,
  tiktokHandle,
  substackHandle,
  bio,
  landscapeImage,
  portraitImages,
  showConsultationButton,
  onClose,
  pricing,
  customHourlyRate,
  onConsultationButtonPress,
  nextAvailableSlot,
  showSubheaderSection,
  outerContainerStyle,
  showPreviewLabel,
  isModalView,
  shareProfileUrl,
  isPreviewView = false,
  customInPersonHourlyRate,
  isAvailableInPerson,
}) => {
  const router = useRouter();
  const { user } = useGetUser();
  const isStylist = useStore((state) => state.isStylist);
  const isDesktop = useIsDesktop();
  const { width } = useDimensions();
  const scrollToTop = useScrollToTop();
  const [isImageLoading, setIsImageLoading] = useState(true);

  const { mobilePreviewContainerWidth, imageContentWidth } = useMemo(() => {
    const mobileWidth = isDesktop
      ? 390
      : isModalView || isPreviewView
        ? width
        : width - 32 * 2;

    const imageWidth = isDesktop
      ? 390 - 24 * 2
      : isModalView || isPreviewView
        ? width - 24 * 2
        : width - 32 * 2 - 24 * 2;

    return {
      mobilePreviewContainerWidth: mobileWidth,
      imageContentWidth: imageWidth,
    };
  }, [isDesktop, isModalView, width]);

  const virtualRateDisplay = useMemo(() => {
    if (customHourlyRate) {
      return `${formatCurrency(customHourlyRate)}/hr`;
    } else if (pricing?.hourlyRate) {
      return `${formatCurrency(pricing.hourlyRate)}/hr`;
    }
    return 'XXX/hr';
  }, [customHourlyRate, pricing?.hourlyRate]);

  const inPersonRateDisplay = useMemo(() => {
    if (customInPersonHourlyRate) {
      return `${formatCurrency(customInPersonHourlyRate)}/hr`;
    }
    return null;
  }, [customInPersonHourlyRate]);

  const hasMultiplePricingOptions =
    isAvailableInPerson && !!inPersonRateDisplay;

  const handleSupportLinkPress = useCallback(() => {
    Linking.openURL(
      `mailto:${EMME_SUPPORT_EMAIL}?subject=Request%20Price%20Tier%20Review`,
    );
  }, []);

  const resizedProfilePicUrl = useMemo(() => {
    return createResizedPhotoUrl(profilePicUrl, FULL_WIDTH);
  }, [profilePicUrl]);

  const formattedNextAvailableDate = useMemo(() => {
    if (!nextAvailableSlot) return null;

    const userTimeZone = user?.timeZone || getCurrentTimeZone();
    const unixTimestamp = parseInt(nextAvailableSlot.startTime);
    const date = fromUnixTime(unixTimestamp);

    // Format in the user's specific timezone
    return formatInTimeZone(date, userTimeZone, 'EEEE, MMMM d');
  }, [nextAvailableSlot?.startTime, user?.timeZone]);

  const backgroundContainerHorizontalPadding = useMemo(() => {
    if (isDesktop) {
      return 24;
    }

    if (isModalView || isPreviewView) {
      return 0;
    }

    return 32;
  }, [isDesktop, isModalView, isPreviewView]);

  return (
    <>
      <ScrollableView>
        <ModalWrapper
          style={{
            ...containerStyles,
          }}
        >
          <OuterContainer
            isDesktop={isDesktop}
            contentContainerStyle={scrollViewContentContainer}
            style={outerContainerStyle}
            isPreviewView={isPreviewView}
          >
            {showSubheaderSection && (
              <SubheaderContainer isDesktop={isDesktop}>
                {!isDesktop && <PageTitle>Your Profile</PageTitle>}
                <SubheaderText isDesktop={isDesktop}>
                  Your profile is the first thing clients see before requesting
                  a consultation. Make sure to keep it up to date. Here&apos;s a
                  glimpse of what they&apos;ll see:
                </SubheaderText>
                <Button
                  onPress={() => router.push(ROUTES.PROFILE.path)}
                  text="Edit Profile"
                  variant="tertiary"
                  size="small"
                  customStyle={{
                    width: 165,
                    height: isDesktop ? 45 : 48,
                  }}
                />
              </SubheaderContainer>
            )}
            <BackgroundContainer
              isDesktop={isDesktop}
              showPreviewLabel={!!showPreviewLabel}
              isModalView={isModalView}
              isPreviewView={isPreviewView}
              style={{
                paddingHorizontal: backgroundContainerHorizontalPadding,
              }}
            >
              {showPreviewLabel && <PreviewLabel>Preview:</PreviewLabel>}
              {shareProfileUrl && !isModalView && (
                <ShareUrlContainer>
                  <ShareProfileUrl shareProfileUrl={shareProfileUrl} />
                </ShareUrlContainer>
              )}
              <MobilePreviewContainer
                isDesktop={isDesktop}
                isPreviewView={isPreviewView}
              >
                {onClose && (
                  <CloseButton onPress={onClose}>
                    <CloseIcon
                      source={require('@/assets/images/CloseGreen.png')}
                      tintColor={green}
                    />
                  </CloseButton>
                )}
                <StyledH1>{stylistName}</StyledH1>
                <PillTicker
                  tags={expertise}
                  maxWidth={isDesktop ? 390 : mobilePreviewContainerWidth}
                />
                <PillTicker
                  tags={aesthetics}
                  maxWidth={isDesktop ? 390 : mobilePreviewContainerWidth}
                />
                <ProfileImageContainer width={imageContentWidth}>
                  {isImageLoading && (
                    <LoadingSpinnerContainer width={imageContentWidth}>
                      <ActivityIndicator size="large" color={green} />
                    </LoadingSpinnerContainer>
                  )}
                  <ProfileImage
                    source={{ uri: resizedProfilePicUrl }}
                    style={{
                      width: imageContentWidth,
                      height: imageContentWidth,
                      opacity: isImageLoading ? 0 : 1,
                    }}
                    onLoad={() => setIsImageLoading(false)}
                    onError={() => setIsImageLoading(false)}
                  />
                  {isAvailableInPerson && (
                    <InPersonBanner>
                      <InPersonBannerText>
                        AVAILABLE IN PERSON
                      </InPersonBannerText>
                    </InPersonBanner>
                  )}
                </ProfileImageContainer>
                <PricingSocialRow>
                  {hasMultiplePricingOptions && inPersonRateDisplay ? (
                    <PricingAccordion
                      virtualRate={virtualRateDisplay}
                      inPersonRate={inPersonRateDisplay}
                    />
                  ) : (
                    <PricingText>{virtualRateDisplay}</PricingText>
                  )}
                  <SocialButtonsContainer>
                    {!!instagramHandle && (
                      <Pressable
                        onPress={() =>
                          openSocialLink(instagramHandle, 'instagram')
                        }
                      >
                        <SocialIcon
                          source={require('@/assets/images/Instagram.png')}
                        />
                      </Pressable>
                    )}
                    {!!tiktokHandle && (
                      <Pressable
                        onPress={() => openSocialLink(tiktokHandle, 'tiktok')}
                      >
                        <SocialIcon
                          source={require('@/assets/images/TikTok.png')}
                        />
                      </Pressable>
                    )}
                    {!!substackHandle && (
                      <Pressable
                        onPress={() =>
                          openSocialLink(substackHandle, 'substack')
                        }
                      >
                        <SocialIcon
                          source={require('@/assets/images/Substack.png')}
                        />
                      </Pressable>
                    )}
                  </SocialButtonsContainer>
                </PricingSocialRow>
                {nextAvailableSlot && formattedNextAvailableDate && (
                  <AvailabilityContainer
                    onPress={onConsultationButtonPress ?? (() => {})}
                  >
                    <AvailabilityIcon
                      source={require('@/assets/images/Availability.png')}
                    />

                    <AvailabilityText>
                      Available Next - {formattedNextAvailableDate}
                    </AvailabilityText>
                  </AvailabilityContainer>
                )}
                <BioContainer>
                  <BioText>{bio}</BioText>
                </BioContainer>
                <GalleryImageDisplay
                  landscapeImage={landscapeImage}
                  portraitImages={portraitImages}
                  imageContentWidth={imageContentWidth}
                />
              </MobilePreviewContainer>
              <BottomContainer>
                {isStylist && !isPreviewView && (
                  <BodyText style={{ fontSize: 14 }}>
                    Questions about your rate?{' '}
                    <ContactSupportText onPress={handleSupportLinkPress}>
                      Contact Support
                    </ContactSupportText>
                  </BodyText>
                )}
                {shareProfileUrl && isModalView && (
                  <ShareUrlContainer isModalView={isModalView}>
                    <ShareProfileUrl shareProfileUrl={shareProfileUrl} />
                  </ShareUrlContainer>
                )}
                <BackToTopButton
                  scrollToTop={scrollToTop}
                  alternateStyle={isPreviewView}
                />
              </BottomContainer>
            </BackgroundContainer>
          </OuterContainer>
        </ModalWrapper>
      </ScrollableView>
      {showConsultationButton && (
        <ConsultationButtonContainer>
          <Button
            onPress={onConsultationButtonPress ?? (() => {})}
            text="Book Consultation"
          />
        </ConsultationButtonContainer>
      )}
    </>
  );
};

export default StylistProfile;

const containerStyles = css({
  flex: 1,
  backgroundColor: sand,
});

const OuterContainer = styled(KeyboardAwareScrollView)<
  IsDesktopProps & { isPreviewView?: boolean }
>(({ isDesktop, isPreviewView }) => ({
  flex: 1,
  paddingBottom: isDesktop ? 32 : 0,
  paddingHorizontal: isDesktop ? 56 : 0,
  backgroundColor: isPreviewView ? green : sand,
}));

const scrollViewContentContainer: StyleProp<ViewStyle> = {
  alignItems: 'center',
};

const SubheaderContainer = styled.View<IsDesktopProps>(({ isDesktop }) => ({
  width: '100%',
  backgroundColor: isDesktop ? white : sand,
  flexDirection: isDesktop ? 'row' : 'column',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingTop: isDesktop ? 0 : IS_WEB ? 36 : 0,
  paddingBottom: isDesktop ? 0 : 36,
  paddingHorizontal: isDesktop ? 0 : 44,
  marginBottom: isDesktop ? 24 : 0,
}));

const SubheaderText = styled(BodyText)<IsDesktopProps>(({ isDesktop }) => ({
  maxWidth: isDesktop ? 600 : '100%',
  color: lightGreen,
  textAlign: isDesktop ? 'left' : 'center',
  marginBottom: isDesktop ? 0 : 16,
}));

const BackgroundContainer = styled.View<
  IsDesktopProps & {
    showPreviewLabel: boolean;
    isModalView?: boolean;
    isPreviewView?: boolean;
  }
>(({ isDesktop, showPreviewLabel, isPreviewView }) => ({
  width: '100%',
  alignItems: 'center',
  backgroundColor: isPreviewView ? green : mint,
  borderRadius: 10,
  paddingTop: isDesktop
    ? showPreviewLabel
      ? 16
      : 50
    : showPreviewLabel
      ? 24
      : 0,
  paddingBottom: 74,
}));

const PreviewLabel = styled(BodyText)({
  fontSize: 15,
  alignSelf: 'flex-start',
  marginBottom: 12,
});

const MobilePreviewContainer = styled.View<
  IsDesktopProps & { isPreviewView?: boolean }
>(({ isDesktop, isPreviewView }) => ({
  flex: 1,
  width: isDesktop ? 390 : '100%',
  alignItems: 'center',
  backgroundColor: sand,
  shadowColor: isPreviewView ? 'transparent' : mintGreen,
  shadowOffset: {
    width: 0,
    height: 4,
  },
  shadowRadius: 20,
  shadowOpacity: 1,
}));

const StyledH1 = styled(H1)({
  fontSize: 50,
  lineHeight: 50,
  textAlign: 'center',
  textTransform: 'uppercase',
  marginTop: 48,
  marginBottom: 20,
});

const ProfileImageContainer = styled.View(({ width }: { width: number }) => ({
  width,
  borderRadius: 10,
  justifyContent: 'center',
  alignItems: 'center',
  marginVertical: 16,
  overflow: 'hidden',
}));

const ProfileImage = styled.Image({
  width: '100%',
});

const InPersonBanner = styled.View({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: 'rgba(0, 41, 27, 0.5)',
  padding: 12,
  borderBottomLeftRadius: 10,
  borderBottomRightRadius: 10,
});

const InPersonBannerText = styled(H4)({
  fontSize: 12,
  color: white,
  fontWeight: 'bold',
  textAlign: 'center',
  lineHeight: 14,
});

const LoadingSpinnerContainer = styled.View<{ width: number }>(({ width }) => ({
  position: 'absolute',
  width,
  height: width,
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1,
}));

const PricingSocialRow = styled.View({
  flexDirection: 'row',
  width: '100%',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  paddingHorizontal: 32,
  marginBottom: 24,
});

const PricingText = styled(BodyText)({
  fontSize: 20,
  color: green,
});

const SocialButtonsContainer = styled.View({
  flexDirection: 'row',
  gap: 10,
});

const SocialIcon = styled.Image({
  width: 34,
  height: 32,
  borderRadius: 50,
});

const AvailabilityContainer = styled.Pressable({
  flexDirection: 'row',
  width: '100%',
  height: 62,
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: mint,
  marginBottom: 28,
});

const AvailabilityIcon = styled.Image({
  width: 22,
  height: 22,
  tintColor: green,
  marginRight: 10,
});

const AvailabilityText = styled(BodyText)({
  fontSize: 14,
  fontWeight: 'bold',
  color: green,
  letterSpacing: 0,
});

const BioContainer = styled.View({
  width: '100%',
  marginBottom: 52,
});

const BioText = styled(BodyText)({
  fontSize: 20,
  lineHeight: 27,
  color: green,
  textAlign: 'center',
  paddingHorizontal: 32,
});

const BottomContainer = styled.View({
  width: '100%',
  alignItems: 'center',
  gap: 60,
  marginTop: 36,
});

const ContactSupportText = styled(BodyText)({
  textDecorationLine: 'underline',
  fontWeight: 'bold',
  fontSize: 14,
});

const ConsultationButtonContainer = styled.View({
  width: '100%',
  paddingHorizontal: 30,
  backgroundColor: white,
  paddingTop: 24,
  paddingBottom: 32,
});

const CloseButton = styled(Pressable)({
  position: 'absolute',
  top: 20,
  right: 20,
  zIndex: 1,
});

const CloseIcon = styled.Image({
  width: 24,
  height: 24,
});

const ShareUrlContainer = styled.View<{ isModalView?: boolean }>(
  ({ isModalView }) => ({
    width: '100%',
    maxWidth: isModalView ? GALLERY_CONSTANTS.MAX_WIDTH : 390,
    marginBottom: isModalView ? 0 : 24,
    alignSelf: 'center',
  }),
);
